import clsx from 'clsx';
import { type RefObject } from 'react';

import { crosshair } from './XYChartCrosshair.sty.js';
import { plotLayout } from '../../../../core/components/chart-grid/ChartGrid.sty.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { HorizontalCrosshair } from '../../../../core/components/crosshair/HorizontalCrosshair.js';
import { VerticalCrosshair } from '../../../../core/components/crosshair/VerticalCrosshair.js';
import { reduceResetExploreVisible } from '../../../../core/components/interactions-state-helpers/reducers/reset-reducers.js';
import { useApplyCallbackOnScroll } from '../../../../core/hooks/useApplyCallbackOnScroll.js';
import { useAxesFormatter } from '../../../hooks/useAxesFormatter.js';
import { useGetThresholds } from '../../../hooks/useGetThresholds.js';
import { useVisibleAxesConfig } from '../../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../../hooks/useVisibleAxesScales.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import {
  useContentState,
  usePositionState,
  useSetState,
} from '../../../store/selectors.js';
import { getCurrentScale } from '../../../utils/get-scale.js';
import { isDotSeriesTooltipContent } from '../../../utils/type-guards.js';
import { xAccessor, yAccessor } from '../../displays/dot-accessors.js';
import { chartCrosshairDataTransformer as transformer } from '../utils/chartCrosshairDataTransformer.js';

export function XYChartCrosshair({
  pinned,
  chartRef,
}: {
  chartRef: RefObject<HTMLDivElement | null>;
  pinned: boolean;
}) {
  const { relative: position } = usePositionState() ?? {};
  const axesScale = useVisibleAxesScales();
  const axesConfig = useVisibleAxesConfig();
  const axesIdToFormatter = useAxesFormatter();
  const { width, height } = useGridPlotSize();
  const { offset: thresholdOffset } = useGetThresholds();

  const chartBounds = chartRef.current?.getBoundingClientRect();
  const tooltipContent = useContentState();
  const axisIdToScales = useXYChartScales();

  const setState = useSetState();
  const onScrollCallback = () => setState(reduceResetExploreVisible);

  useApplyCallbackOnScroll(pinned, onScrollCallback);

  if (!position) {
    return null;
  }

  const { labels } = transformer({
    position,
    pinned,
    axesScale,
    axesIdToFormatter,
    axesConfig,
    width,
  });

  const isBottomLabelRotated =
    axesConfig['bottom']?.tickLabelLayout === 'vertical';
  const isTopLabelRotated = axesConfig['top']?.tickLabelLayout === 'vertical';

  const coordinates =
    pinned && isDotSeriesTooltipContent(tooltipContent)
      ? {
          x: xAccessor(
            getCurrentScale(axisIdToScales, tooltipContent.series.xAxisId),
          )(tooltipContent.datapoint),
          y: yAccessor(
            getCurrentScale(axisIdToScales, tooltipContent.series.yAxisId),
          )(tooltipContent.datapoint),
        }
      : position;

  const leftOffset = labels.left ? thresholdOffset.left : 0;
  const rightOffset = labels.right ? thresholdOffset.right : 0;
  const widthWithOffset = width + leftOffset + rightOffset;

  return (
    <svg className={clsx(plotLayout, crosshair)}>
      <HorizontalCrosshair
        dashed={!pinned}
        data-testid={'horizontal-crosshair-xy-chart'}
        leftLabel={labels.left}
        rightLabel={labels.right}
        y={coordinates.y}
        width={widthWithOffset}
        height={height}
        chartBounds={chartBounds}
        leftOffset={leftOffset}
        rightOffset={rightOffset}
      />
      <VerticalCrosshair
        dashed={!pinned}
        data-testid="vertical-crosshair-xy-chart"
        bottomLabel={labels.bottom}
        topLabel={labels.top}
        isBottomLabelRotated={isBottomLabelRotated}
        isTopLabelRotated={isTopLabelRotated}
        x={coordinates.x}
        height={height}
        width={widthWithOffset}
        chartBounds={chartBounds}
      />
    </svg>
  );
}
